import "./App.css";
import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import { Toaster, ToastBar } from "react-hot-toast";
import { updateUser } from "./actions/users";
import { loadStockData } from "./actions/loadStockData";
import InternetConnectionMonitor from "./pages/Internet Connection/InternetConnection";
import AppLayout from "./ui/AppLayout";
import ProtectedRoute from "./ui/ProtectedRoute";
import { PageLoading } from "./ui/LoadingState";
import { GlobalLoadingBar, LoadingToast } from "./ui/LoadingToast";

// Pages load when first opened
const Home = lazy(() => import("./pages/Home/Home"));
const StockHome = lazy(() => import("./pages/Stock/StockTabs"));
const UserAccountPage = lazy(() => import("./user page/UserAccountPage"));
const Login = lazy(() => import("./pages/Login/login"));
const StockReport = lazy(() => import("./pages/Report/Report"));
const ResetPassword = lazy(() => import("./pages/Login/ResetPassword"));
const ResetMyPassword = lazy(() => import("./user page/ResetPassword"));
const AllRequest = lazy(() => import("./pages/All Request/AllRequest"));
const RequestStock = lazy(() => import("./pages/Request/Request"));
const MainStoreRequisition = lazy(() => import("./pages/Main Store Requisition/MainStoreRequisition"));
const RequisitionApprovals = lazy(() => import("./pages/Requisition Approvals/RequisitionApprovals"));
const BranchStockReport = lazy(() => import("./pages/Branch Report/Branch Report"));
const NotFound = lazy(() => import("./pages/Not Found/NotFound"));
const BranchReceivedHome = lazy(() => import("./pages/Branch Home/Branch Home"));
const ConsoleStockReport = lazy(() => import("./pages/Consolidate Stock Report/Report"));
const ConsoleIssueReport = lazy(() => import("./pages/Consolidated Issue Report/Report"));
const ConsoleReceiptReport = lazy(() => import("./pages/Consolidated Receipt Report/Report"));
const ForecastPage = lazy(() => import("./pages/ForecastPage/Forecast"));
const RequisitionReport = lazy(() => import("./pages/Requisition Report/Requisition Report"));
const LocationStatement = lazy(() => import("./pages/Location Statement/LocationStatement"));

function App() {
  const [user] = React.useState(() =>
    JSON.parse(secureLocalStorage.getItem("profile")),
  );
  const isAdmin = user?.results?.stock === "Administrator";
  const isApprover = user?.results?.stock === "Approver";

  const dispatch = useDispatch();
  React.useEffect(() => {
    if (user) {
      dispatch(loadStockData(isAdmin, isApprover));
      dispatch(updateUser(user?.results._id, { login: "yes", system: "stock" }));
    }
  }, [dispatch, user, isAdmin, isApprover]);

  // Head office pages
  const adminRoutes = [
    ["/stock/all-request", <AllRequest />],
    ["/stock/consol/report", <ConsoleStockReport />],
    ["/stock/consol/issue/report", <ConsoleIssueReport />],
    ["/stock/consol/receipt/report", <ConsoleReceiptReport />],
    ["/stock/requisition-report", <RequisitionReport />],
    ["/stock/location-report", <BranchStockReport />],
    ["/stock/location-statement", <LocationStatement />],
    ["/stock/forecast", <ForecastPage />],
  ];

  return (
    <div className="App">
      <InternetConnectionMonitor />
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* an Approver's work is the approvals page */}
            <Route
              path="/stock/"
              element={isApprover ? <Navigate replace to="/stock/requisition-approvals" /> : <Home />}
            />
            <Route index element={<Navigate replace to={"/stock/"} />} />
            <Route path="/stock/stock-home" element={<StockHome />} />
            <Route path="/stock/settings" element={<UserAccountPage />} />
            <Route path="/stock/report" element={<StockReport />} />
            <Route
              path="/stock/request"
              element={isAdmin ? <MainStoreRequisition /> : <RequestStock />}
            />
            <Route
              path="/stock/requisition-approvals"
              element={isAdmin || isApprover ? <RequisitionApprovals /> : <Navigate replace to="/stock/" />}
            />
            {/* always defined, so a deep link works before the login is picked up */}
            <Route path="/stock/:branch/new-items" element={<BranchReceivedHome />} />
            {adminRoutes.map(([path, element]) => (
              <Route
                key={path}
                path={path}
                element={isAdmin ? element : <Navigate replace to="/stock/" />}
              />
            ))}
          </Route>

          <Route path="/stock/login" element={<Login />} />
          <Route path="/stock/resetPassword" element={<ResetPassword />} />
          <Route path="/stock/resetMyPassword/:token" element={<ResetMyPassword />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster
        position="top-center"
        gutter={12}
        containerStyle={{ margin: "8px" }}
        toastOptions={{
          success: { duration: 3000 },
          error: { duration: 6000 },
          style: {
            fontSize: "13.5px",
            maxWidth: "480px",
            padding: "10px 16px",
            borderRadius: "10px",
            background: "#fff",
            color: "#1f2937",
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
          },
        }}
      >
        {/* Loading toasts show as the app's loading indicator (ui/LoadingToast) */}
        {(t) => (t.type === "loading" ? <LoadingToast toast={t} /> : <ToastBar toast={t} />)}
      </Toaster>
      <GlobalLoadingBar />
    </div>
  );
}

export default App;
